(function () {
  const elementName = 'occasion-slider';

  if (customElements.get(elementName)) return;

  class OccasionSlider extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.currentTranslate = 0;
      this.prevTranslate = 0;
      this.isPointerDown = false;
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragStartY = 0;
      this.autoplayTimer = null;
      this.wheelDelta = 0;
      this.wheelLocked = false;
      this.wheelUnlockTimer = null;

      this.handleResize = this.handleResize.bind(this);
      this.handlePrevClick = this.handlePrevClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handleDragStart = this.handleDragStart.bind(this);
      this.handleDragMove = this.handleDragMove.bind(this);
      this.handleDragEnd = this.handleDragEnd.bind(this);
      this.handleDragCancel = this.handleDragCancel.bind(this);
      this.handleWheel = this.handleWheel.bind(this);
      this.handleTrackTransitionEnd = this.handleTrackTransitionEnd.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.viewport = this.querySelector('.occasion-slider__viewport');
      this.prevButton = this.querySelector('[data-slider-prev]');
      this.nextButton = this.querySelector('[data-slider-next]');
      this.dotsContainer = this.querySelector('[data-slider-dots]');
      this.realCards = Array.from(this.querySelectorAll('[data-slider-card]:not([data-clone="true"])'));

      if (!this.track || this.realCards.length === 0) return;

      this.settings = {
        sliderSpeed: this.getNumberSetting('sliderSpeed', 600),
        gap: this.getNumberSetting('cardGap', 20),
        desktopCardWidth: this.getNumberSetting('desktopCardWidth', 320),
        desktopCardHeight: this.getNumberSetting('desktopCardHeight', 480),
        mobileCardWidth: this.getNumberSetting('mobileCardWidth', 280),
        mobileCardHeight: this.getNumberSetting('mobileCardHeight', 420),
        autoplayEnabled: this.dataset.autoplayEnabled === 'true',
        autoplaySpeed: this.getNumberSetting('autoplaySpeed', 4000),
        desktopVisibleCards: this.getNumberSetting('desktopVisibleCards', 5)
      };

      this.isLooping = this.realCards.length > 1;
      this.edgeCloneCount = this.isLooping ? Math.min(2, this.realCards.length) : 0;

      this.prepareTrack();
      this.renderDots();
      this.setupSliderMetrics();

      this.currentIndex = this.isLooping ? this.edgeCloneCount : 0;
      if (this.isLooping && !this.isMobile && this.realCards.length >= this.settings.desktopVisibleCards) {
        this.currentIndex = this.edgeCloneCount + Math.floor(this.settings.desktopVisibleCards / 2);
      }

      this.setupEventListeners();
      this.updateSlider(false);
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.wheelUnlockTimer) {
        clearTimeout(this.wheelUnlockTimer);
        this.wheelUnlockTimer = null;
      }

      if (this.prevButton) this.prevButton.removeEventListener('click', this.handlePrevClick);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.handleNextClick);

      if (this.track) {
        this.track.removeEventListener('mousedown', this.handleDragStart);
        this.track.removeEventListener('touchstart', this.handleDragStart);
        this.track.removeEventListener('mousemove', this.handleDragMove);
        this.track.removeEventListener('touchmove', this.handleDragMove);
        this.track.removeEventListener('mouseup', this.handleDragEnd);
        this.track.removeEventListener('touchend', this.handleDragEnd);
        this.track.removeEventListener('touchcancel', this.handleDragCancel);
        this.track.removeEventListener('mouseleave', this.handleDragEnd);
        this.track.removeEventListener('transitionend', this.handleTrackTransitionEnd);
      }

      if (this.viewport) {
        this.viewport.removeEventListener('wheel', this.handleWheel);
      }

      window.removeEventListener('resize', this.handleResize);
    }

    getNumberSetting(key, fallback) {
      const value = Number(this.dataset[key]);
      return Number.isFinite(value) ? value : fallback;
    }

    getPointerPosition(event) {
      if (event.type.startsWith('touch')) {
        const touch = event.touches[0] || event.changedTouches[0];
        if (!touch) return null;

        return {
          x: touch.clientX,
          y: touch.clientY
        };
      }

      return {
        x: event.pageX,
        y: event.pageY
      };
    }

    startDragging() {
      if (this.isDragging) return;

      this.isDragging = true;
      this.track.style.cursor = 'grabbing';
      this.track.style.transition = 'none';
      this.stopAutoplay();
    }

    resetDragState() {
      this.isPointerDown = false;
      this.isDragging = false;
    }

    prepareTrack() {
      if (!this.isLooping) {
        this.cards = this.realCards;
        return;
      }

      if (!this.track.querySelector('[data-clone="true"]')) {
        const leadingFragment = document.createDocumentFragment();
        this.realCards
          .slice(this.realCards.length - this.edgeCloneCount)
          .forEach((card) => {
            const clone = card.cloneNode(true);
            clone.setAttribute('data-clone', 'true');
            leadingFragment.appendChild(clone);
          });
        this.track.insertBefore(leadingFragment, this.track.firstChild);

        const trailingFragment = document.createDocumentFragment();
        this.realCards
          .slice(0, this.edgeCloneCount)
          .forEach((card) => {
            const clone = card.cloneNode(true);
            clone.setAttribute('data-clone', 'true');
            trailingFragment.appendChild(clone);
          });
        this.track.appendChild(trailingFragment);
      }

      this.cards = Array.from(this.track.querySelectorAll('[data-slider-card]'));
    }

    renderDots() {
      if (!this.dotsContainer) return;

      this.dotsContainer.innerHTML = '';
      const fragment = document.createDocumentFragment();

      this.realCards.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'occasion-slider__dot';
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
        dot.addEventListener('click', () => this.goToSlide(index));
        fragment.appendChild(dot);
      });

      this.dotsContainer.appendChild(fragment);
      this.dots = Array.from(this.dotsContainer.querySelectorAll('.occasion-slider__dot'));
    }

    setupSliderMetrics() {
      this.isMobile = window.innerWidth < 750;

      const containerWidth = this.track.parentElement.offsetWidth;
      const trackPaddingLeft = parseFloat(window.getComputedStyle(this.track).paddingLeft) || 0;

      let cardWidth = this.isMobile ? this.settings.mobileCardWidth : this.settings.desktopCardWidth;
      let cardHeight = this.isMobile ? this.settings.mobileCardHeight : this.settings.desktopCardHeight;

      if (!this.isMobile && this.realCards.length > 0) {
        const cardsPerRow = Math.min(this.settings.desktopVisibleCards, this.realCards.length);
        cardWidth = Math.max(140, (containerWidth - this.settings.gap * (cardsPerRow - 1)) / cardsPerRow);
        cardHeight = Math.round((cardWidth / this.settings.desktopCardWidth) * this.settings.desktopCardHeight);
      }

      this.cardWidth = cardWidth;
      this.cardHeight = cardHeight;
      this.centerOffset = (containerWidth - cardWidth) / 2 - trackPaddingLeft;

      this.cards.forEach((card) => {
        card.style.width = `${cardWidth}px`;
        card.style.height = `${cardHeight}px`;
      });
    }

    setupEventListeners() {
      if (this.prevButton) this.prevButton.addEventListener('click', this.handlePrevClick);
      if (this.nextButton) this.nextButton.addEventListener('click', this.handleNextClick);

      this.track.addEventListener('mousedown', this.handleDragStart);
      this.track.addEventListener('touchstart', this.handleDragStart, { passive: true });
      this.track.addEventListener('mousemove', this.handleDragMove);
      this.track.addEventListener('touchmove', this.handleDragMove, { passive: false });
      this.track.addEventListener('mouseup', this.handleDragEnd);
      this.track.addEventListener('touchend', this.handleDragEnd);
      this.track.addEventListener('touchcancel', this.handleDragCancel);
      this.track.addEventListener('mouseleave', this.handleDragEnd);
      this.track.addEventListener('transitionend', this.handleTrackTransitionEnd);

      if (this.viewport) {
        this.viewport.addEventListener('wheel', this.handleWheel, { passive: false });
      }

      window.addEventListener('resize', this.handleResize);
    }

    handleResize() {
      const realIndex = this.getRealIndex();
      this.setupSliderMetrics();
      this.currentIndex = this.isLooping ? realIndex + this.edgeCloneCount : realIndex;
      this.updateSlider(false);
    }

    handlePrevClick() {
      this.prev();
      this.restartAutoplay();
    }

    handleNextClick() {
      this.next();
      this.restartAutoplay();
    }

    handleDragStart(event) {
      if (this.cards.length <= 1) return;
      if (event.type === 'mousedown' && event.button !== 0) return;

      const pointer = this.getPointerPosition(event);
      if (!pointer) return;

      this.isPointerDown = true;
      this.dragStartX = pointer.x;
      this.dragStartY = pointer.y;
      this.currentTranslate = this.prevTranslate;

      if (event.type.includes('mouse')) {
        this.startDragging();
      }
    }

    handleDragMove(event) {
      if (!this.isPointerDown) return;

      if (event.type === 'mousemove' && event.buttons === 0) {
        this.handleDragEnd();
        return;
      }

      const pointer = this.getPointerPosition(event);
      if (!pointer) return;

      const distanceX = pointer.x - this.dragStartX;
      const distanceY = pointer.y - this.dragStartY;

      if (event.type === 'touchmove' && !this.isDragging) {
        if (Math.abs(distanceX) < 8 && Math.abs(distanceY) < 8) return;

        if (Math.abs(distanceY) > Math.abs(distanceX)) {
          this.resetDragState();
          this.track.style.cursor = 'grab';
          this.track.style.transition = '';
          return;
        }

        this.startDragging();
      }

      if (!this.isDragging) return;

      if (event.type === 'touchmove') {
        event.preventDefault();
      }

      this.currentTranslate = this.prevTranslate + distanceX;
      this.track.style.transform = `translateX(${this.currentTranslate}px)`;
    }

    handleDragEnd() {
      if (!this.isPointerDown && !this.isDragging) return;

      const wasDragging = this.isDragging;

      this.resetDragState();
      this.track.style.cursor = 'grab';
      this.track.style.transition = '';

      if (!wasDragging) return;

      const movedBy = this.currentTranslate - this.prevTranslate;
      if (movedBy < -50) {
        this.next();
      } else if (movedBy > 50) {
        this.prev();
      } else {
        this.updateSlider();
      }

      this.restartAutoplay();
    }

    handleDragCancel() {
      if (!this.isPointerDown && !this.isDragging) return;

      const wasDragging = this.isDragging;

      this.resetDragState();
      this.track.style.cursor = 'grab';
      this.track.style.transition = '';
      this.updateSlider();

      if (wasDragging) {
        this.restartAutoplay();
      }
    }

    handleWheel(event) {
      if (this.cards.length <= 1) return;

      const horizontalDelta = event.deltaX;
      const verticalDelta = event.deltaY;

      if (Math.abs(horizontalDelta) <= Math.abs(verticalDelta) || Math.abs(horizontalDelta) < 5) {
        this.wheelDelta = 0;
        return;
      }

      event.preventDefault();
      this.wheelDelta += horizontalDelta;

      if (this.wheelLocked || Math.abs(this.wheelDelta) < 45) return;

      if (this.wheelDelta > 0) {
        this.next();
      } else {
        this.prev();
      }

      this.wheelDelta = 0;
      this.wheelLocked = true;

      if (this.wheelUnlockTimer) {
        clearTimeout(this.wheelUnlockTimer);
      }

      this.wheelUnlockTimer = setTimeout(() => {
        this.wheelLocked = false;
      }, Math.max(120, Math.min(400, this.settings.sliderSpeed)));

      this.restartAutoplay();
    }

    prev() {
      if (this.cards.length <= 1) return;
      if (this.isLooping || this.currentIndex > 0) {
        this.currentIndex -= 1;
        this.updateSlider();
      }
    }

    next() {
      if (this.cards.length <= 1) return;
      if (this.isLooping || this.currentIndex < this.cards.length - 1) {
        this.currentIndex += 1;
        this.updateSlider();
      }
    }

    goToSlide(index) {
      this.currentIndex = this.isLooping ? index + this.edgeCloneCount : index;
      this.updateSlider();
      this.restartAutoplay();
    }

    mapIndexToReal(index) {
      if (!this.isLooping) return index;
      const total = this.realCards.length;
      return ((index - this.edgeCloneCount) % total + total) % total;
    }

    getRealIndex() {
      return this.mapIndexToReal(this.currentIndex);
    }

    updateSlider(animate = true, isSnap = false) {
      const offset = -(this.currentIndex * (this.cardWidth + this.settings.gap)) + this.centerOffset;

      if (isSnap) {
        this.track.classList.add('is-snapping');
      }
      if (!animate) {
        this.track.style.transition = 'none';
      }

      this.track.style.transform = `translateX(${offset}px)`;
      this.prevTranslate = offset;
      this.currentTranslate = offset;

      this.updateCardStates();
      this.updateDots();
      this.updateArrowState();

      if (!animate) {
        this.track.offsetHeight;
        requestAnimationFrame(() => {
          this.track.style.transition = '';
          if (isSnap) this.track.classList.remove('is-snapping');
        });
      } else if (isSnap) {
        requestAnimationFrame(() => {
          this.track.classList.remove('is-snapping');
        });
      }
    }

    updateCardStates() {
      const realIndex = this.getRealIndex();
      const total = this.realCards.length;
      const previousIndex = (realIndex - 1 + total) % total;
      const nextIndex = (realIndex + 1) % total;

      this.cards.forEach((card, index) => {
        card.classList.toggle('is-active', index === this.currentIndex);

        const cardRealIndex = this.mapIndexToReal(index);
        const isNear =
          !this.isMobile &&
          total >= 3 &&
          index !== this.currentIndex &&
          (cardRealIndex === previousIndex || cardRealIndex === nextIndex);

        card.classList.toggle('is-near', isNear);
      });
    }

    updateDots() {
      if (!this.dots || this.dots.length === 0) return;

      const activeIndex = this.getRealIndex();
      this.dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === activeIndex);
      });
    }

    updateArrowState() {
      const singleSlide = this.cards.length <= 1;

      if (this.prevButton) {
        this.prevButton.disabled = singleSlide || (!this.isLooping && this.currentIndex === 0);
      }

      if (this.nextButton) {
        this.nextButton.disabled = singleSlide || (!this.isLooping && this.currentIndex === this.cards.length - 1);
      }
    }

    handleTrackTransitionEnd(event) {
      if (!this.isLooping || event.propertyName !== 'transform') return;

      const total = this.realCards.length;

      if (this.currentIndex < this.edgeCloneCount) {
        this.currentIndex += total;
        this.updateSlider(false, true);
      } else if (this.currentIndex >= this.edgeCloneCount + total) {
        this.currentIndex -= total;
        this.updateSlider(false, true);
      }
    }

    startAutoplay() {
      if (!this.settings.autoplayEnabled || this.cards.length <= 1) return;

      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        this.next();
      }, this.settings.autoplaySpeed);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;

      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  customElements.define(elementName, OccasionSlider);
})();
